import { useState } from 'react';
import { Check } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { Tournament } from '@/data/types';

interface Props {
  tournament: Tournament;
}

const FORMAT_LABELS: Record<string, string> = {
  SINGLE_ELIM: 'Олимпийка',
  DOUBLE_ELIM: 'Двойная олимпийка',
  ROUND_ROBIN: 'Круговая',
  SWISS: 'Швейцарка',
  GROUPS_PLAYOFF: 'Группы + плей-офф',
};

function dateToInput(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function TournamentSettingsTab({ tournament }: Props) {
  const initialName = tournament.name;
  const initialDate = dateToInput(tournament.date.toDate());

  const [name, setName] = useState(initialName);
  const [date, setDate] = useState(initialDate);
  const [busy, setBusy] = useState(false);

  const dirty = name !== initialName || date !== initialDate;

  async function handleSave() {
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error('Название не может быть пустым');
      return;
    }
    const [y, m, d] = date.split('-').map(Number);
    setBusy(true);
    try {
      await updateDoc(doc(db, 'tournaments', tournament.id), {
        name: trimmedName,
        date: new Date(y, m - 1, d),
        updatedAt: serverTimestamp(),
      });
      toast.success('Сохранено');
    } catch (err) {
      toast.error('Не получилось', { description: String((err as Error).message) });
    } finally {
      setBusy(false);
    }
  }

  function handleCancel() {
    setName(initialName);
    setDate(initialDate);
  }

  const params: { label: string; value: string }[] = [
    { label: 'Формат', value: FORMAT_LABELS[tournament.format] ?? tournament.format },
    {
      label: 'Дисциплина',
      value: tournament.discipline === 'SINGLES' ? 'Одиночка' : 'Пары',
    },
    { label: 'Формат матча', value: tournament.matchFormat },
    {
      label: 'Учёт счёта',
      value: tournament.scoreMode === 'WINNER_ONLY' ? 'Только победитель' : 'По сетам',
    },
  ];

  return (
    <div className="space-y-4">
      <div className="card-d card-pad">
        <div className="t-eyebrow">Основные настройки</div>
        <div style={{ marginTop: 14 }}>
          <label className="label">Название</label>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div style={{ marginTop: 14 }}>
          <label className="label">Дата</label>
          <input
            className="input"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <div className="row gap-2" style={{ marginTop: 16, justifyContent: 'flex-end' }}>
          <button className="btn" onClick={handleCancel} disabled={!dirty}>
            Отменить
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={busy || !name.trim()}
          >
            {busy ? <span className="spinner" /> : <Check size={14} />}
            Сохранить
          </button>
        </div>
      </div>

      <div className="card-d card-pad">
        <div className="t-eyebrow">Параметры турнира</div>
        <div
          style={{
            marginTop: 14,
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 10,
          }}
        >
          {params.map((p) => (
            <div
              key={p.label}
              style={{
                padding: '10px 12px',
                background: 'var(--bg-sunken)',
                borderRadius: 'var(--r-2)',
              }}
            >
              <div className="t-xs t-mute">{p.label}</div>
              <div className="t-small" style={{ marginTop: 2 }}>
                {p.value}
              </div>
            </div>
          ))}
        </div>
        <div className="help" style={{ marginTop: 12 }}>
          Параметры формата нельзя изменить после создания.
        </div>
      </div>

      <div className="card-d card-pad" style={{ borderColor: 'var(--red-soft)' }}>
        <div className="t-eyebrow" style={{ color: 'var(--red)' }}>
          Опасная зона
        </div>
        <div className="t-small t-mute" style={{ marginTop: 8 }}>
          Удаление турнира доступно в шапке страницы — кнопка «Удалить турнир». Все участники,
          матчи и результаты будут удалены безвозвратно.
        </div>
      </div>
    </div>
  );
}
